import React, { useMemo, useState } from "react";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../../helpers/api/api_interceptor";

const initialFormState = {
  reportType: "bug",
  priority: "Medium",
  description: "",
};

const BugReportWidget = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const userId = useSelector((state) => state?.User?.userData?.id);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [files, setFiles] = useState([]);
  const [pageUrl, setPageUrl] = useState("");

  const reportTypes = useMemo(
    () => [
      { value: "bug", label: t("feedback.reportTypeBug") },
      { value: "improvement", label: t("feedback.reportTypeImprovement") },
      { value: "feature", label: t("feedback.reportTypeFeature") },
    ],
    [t]
  );

  const priorities = useMemo(
    () => [
      { value: "Critical", label: t("feedback.priorityCritical") },
      { value: "Urgent", label: t("feedback.priorityUrgent") },
      { value: "Medium", label: t("feedback.priorityMedium") },
      { value: "Low", label: t("feedback.priorityLow") },
    ],
    [t]
  );

  const toggle = () => {
    if (!isSubmitting) {
      setIsOpen((prev) => !prev);
      if (!isOpen) setPageUrl(window.location.href);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    setFiles(Array.from(event.target.files || []));
  };

  const resetForm = () => {
    setFormState(initialFormState);
    setFiles([]);
  };

  const submitDisabled = useMemo(() => {
    return !formState.description.trim();
  }, [formState]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitDisabled || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("report_type", formState.reportType);
      formData.append("priority", formState.priority);
      formData.append("description", formState.description.trim());
      formData.append("page_url", pageUrl || window.location.href);
      formData.append("user_agent", window.navigator.userAgent || "");
      if (userId) formData.append("user_id", String(userId));
      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await API.post("/reports/feedback", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response && response.status === 200) {
        toast.success(response?.data?.detail || t("feedback.toastSuccess"));
        resetForm();
        setIsOpen(false);
      } else {
        toast.error(t("feedback.toastError"));
      }
    } catch (error) {
      toast.error(t("feedback.toastError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (location.pathname === "/login") return null;

  return (
    <>
      <button type="button" className="bug-report-fab" onClick={toggle} aria-label={t("feedback.ariaButton")}>
        !
      </button>
      <Modal centered isOpen={isOpen} toggle={toggle} fade={false}>
        <ModalHeader toggle={toggle}>{t("feedback.modalTitle")}</ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="reportType">{t("feedback.typeLabel")}</Label>
              <Input id="reportType" name="reportType" type="select" value={formState.reportType} onChange={handleChange}>
                {reportTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="description">{t("feedback.descriptionLabel")}</Label>
              <Input
                id="description"
                name="description"
                type="textarea"
                rows="4"
                placeholder={t("feedback.descriptionPlaceholder")}
                value={formState.description}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="priority">{t("feedback.priorityLabel")}</Label>
              <Input id="priority" name="priority" type="select" value={formState.priority} onChange={handleChange}>
                {priorities.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="files">{t("feedback.filesLabel")}</Label>
              <Input id="files" name="files" type="file" multiple onChange={handleFileChange} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={toggle} disabled={isSubmitting}>
              {t("feedback.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={submitDisabled || isSubmitting} className="btn-load d-flex">
              {isSubmitting ? (
                <span className="d-flex align-items-center">
                  <Spinner size="sm" className="flex-shrink-0" />
                </span>
              ) : null}
              <span className={`flex-grow-1 ${isSubmitting ? "ms-2" : ""}`}>{t("feedback.submit")}</span>
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default BugReportWidget;
